import identity from 'lodash/identity';
import pickBy from 'lodash/pickBy';
import { type Nullable } from '@direct-frontend/stdlib';
import { createApi, createBaseQuery } from '@adv-frontend/modules-query';
import {
    CheckDeliveryAvailableToNewDestinationMethod,
    type CheckDeliveryAvailableToNewDestinationResponseSuccess,
    type CheckDeliveryAvailableToNewDestinationSearchParams,
    getCheckDeliveryAvailableToNewDestinationUrl,
    GetDeliveryOptionsMethod,
    type GetDeliveryOptionsPathParameters,
    type GetDeliveryOptionsSearchParams,
    getGetDeliveryOptionsUrl,
    type SchemaDeliveryOptions,
} from '@/shared/api/openApi';
import {
    type AggregatedOption,
    type OrderDeliveryOptionDto,
    type OrderDeliveryOptions,
    type ServiceType,
} from '@/shared/api/order';
import { type OrderDeliveryOption } from '@/shared/api/order';
import { request } from '@/shared/api/request';
import { getRequestContext } from '@/shared/lib/requestContext';
import { type AggregatedOptionsDto, type DeliveryOptionsResponse } from '../types/DeliveryOptionsResponse';

type DeliveryOptionsRequestParams = Omit<GetDeliveryOptionsSearchParams, 'service_type'> &
    GetDeliveryOptionsPathParameters & {
        service_type?: ServiceType;
    };

const toOptionsModel = (option: OrderDeliveryOptionDto): OrderDeliveryOption => {
    return {
        method: option.method,
        intervals: option.intervals.map((interval) => {
            return {
                from: interval.from,
                to: interval.to,
                serviceType: interval.service_type,
                price: interval.price,
            };
        }),
        price: option.price,
    };
};

const toDeliveryMethodModel = (method: AggregatedOptionsDto): AggregatedOption => {
    return {
        method: method.method,
        isAvailable: method.is_available,
        interval: {
            from: method.interval.from,
            to: method.interval.to,
            serviceType: method.interval.service_type,
            price: method.interval.price,
        },
    };
};

const transformOptionsResponse = (response: DeliveryOptionsResponse): OrderDeliveryOptions => {
    return {
        deliveryMethods: {
            pickupPoint: toDeliveryMethodModel(response.aggregated_options[0]),
            courier: toDeliveryMethodModel(response.aggregated_options[1]),
            selfPickupPoint: toDeliveryMethodModel(response.aggregated_options[2]),
        },
        deliveryChunksOptions: response.delivery_chunks_options.map((chunk) => {
            return {
                chunkId: chunk.delivery_chunk_id,
                options: chunk.options.map(toOptionsModel),
            };
        }),
        isDeliveryChunksChanged: response.is_delivery_chunks_changed,
    };
};

const deliveryOptionsApi = createApi({
    name: 'entities/order/api/deliveryOptionsApi',
    baseQuery: createBaseQuery(request),
    endpoints: (builder) => {
        return {
            CheckDeliveryAvailable: builder.query<
                CheckDeliveryAvailableToNewDestinationResponseSuccess,
                CheckDeliveryAvailableToNewDestinationSearchParams
            >({
                query: (params) => {
                    const { orderId, storeSlug } = getRequestContext();

                    return {
                        method: CheckDeliveryAvailableToNewDestinationMethod,
                        params,
                        url: getCheckDeliveryAvailableToNewDestinationUrl({ order_id: orderId, store_slug: storeSlug }),
                    };
                },
            }),
            DeliveryOptions: builder.query<OrderDeliveryOptions, DeliveryOptionsRequestParams>({
                query: (payload) => {
                    return {
                        url: getGetDeliveryOptionsUrl(payload),
                        method: GetDeliveryOptionsMethod,
                        params: pickBy(
                            {
                                address: payload.address,
                                locality_only: payload.locality_only,
                                pickup_point_id: payload.pickup_point_id,
                                service_type: payload.pickup_point_id ? payload.service_type : undefined,
                            },
                            identity
                        ),
                    };
                },
                transformResponse: transformOptionsResponse,
            }),
        };
    },
});

type OrderDeliveryOptionsParams = {
    orderId: string;
    storeSlug: string;
    locality: Nullable<string>;
    detailAddress?: Nullable<string>;
    pickupPointId?: string;
    serviceType?: ServiceType;
};

const mapOrderDeliveryOptionsParams = (params: OrderDeliveryOptionsParams): DeliveryOptionsRequestParams => {
    const { orderId, storeSlug, locality, pickupPointId, detailAddress, serviceType } = params;

    const pickupParams: Pick<DeliveryOptionsRequestParams, 'pickup_point_id' | 'service_type'> = {};

    //  pickupPointId и serviceType всегда должны посылаться только вместе
    if (pickupPointId && serviceType) {
        // undefined и пустая строка будут обработаны в ендпоинте одинаково,
        // но для кеширования запроса это две разные вещи
        pickupParams.pickup_point_id = pickupPointId ?? '';
        pickupParams.service_type = serviceType;
    }

    const payload: DeliveryOptionsRequestParams = {
        order_id: orderId,
        store_slug: storeSlug,
        locality_only: !detailAddress,
        address: detailAddress ? `${locality}, ${detailAddress}` : (locality ?? undefined),
        ...pickupParams,
    };

    return payload;
};

export async function getOrderDeliveryOptions(
    params: OrderDeliveryOptionsParams,
    options: { forceRefetch?: boolean } = {}
): Promise<OrderDeliveryOptions> {
    if (!params.locality) {
        return {
            deliveryMethods: {},
            deliveryChunksOptions: [],
            isDeliveryChunksChanged: false,
        };
    }

    const queryArgs = mapOrderDeliveryOptionsParams(params);

    return await deliveryOptionsApi.endpoints.DeliveryOptions.query(queryArgs, { forceRefetch: options.forceRefetch });
}

export const upsertDeliveryOptions = async (params: OrderDeliveryOptionsParams, data: SchemaDeliveryOptions) => {
    const queryArgs = mapOrderDeliveryOptionsParams(params);
    const transformedData = transformOptionsResponse(data as DeliveryOptionsResponse);

    await deliveryOptionsApi.endpoints.DeliveryOptions.upsertQueryData(queryArgs, transformedData);

    return transformedData;
};

export const checkDeliveryAvailable = deliveryOptionsApi.endpoints.CheckDeliveryAvailable.query;
